import { FORM_NAMES } from 'constants/forms';
import { getFormValues, reset, startSubmit, stopSubmit } from 'redux-form';
import { put, race, select, takeEvery } from 'redux-saga/effects';
import { hide as hideModal } from 'redux-modal';
import i18n from 'services/i18n';
import {
  requestChangePassword,
  requestSendFeedback,
  userSubmitChangePasswordForm,
  userSubmitFeedbackForm,
  userSubmitNotificationsForm,
  userSetResource,
  userAcceptAgreement,
  requestPutUserData,
} from 'store/actionCreators/user';
import { requestPutPatientById } from 'store/actionCreators/patient';
import { getUniqId } from 'utilities';
import { createSagaRequestRace } from 'utilities/request';

import {
  getPatientAcceptAgreementData,
  getEditedPatientsNotificationsData,
  getUserId,
  getUserResourceType,
} from 'store/selectors/user';
import { notificationsAdd } from 'virtues-bootstrap';

import { ACCEPT_USER_AGREEMENT } from 'constants/modals';

export function* handleSubmitChangeForm() {
  yield put(startSubmit(FORM_NAMES.changePassword));
  const { currentPassword, newPassword } = yield select(getFormValues(FORM_NAMES.changePassword));

  yield put(requestChangePassword({ currentPassword, newPassword }));
  const { success } = yield race(createSagaRequestRace(requestChangePassword));
  if (success) {
    yield put(reset(FORM_NAMES.changePassword));
    yield put(
      notificationsAdd({
        id: getUniqId(),
        variant: 'success',
        title: i18n.t('common:requestSuccess'),
        description: i18n.t('common:patient.successChangePassword'),
      }),
    );
    yield put(stopSubmit(FORM_NAMES.changePassword));
  } else {
    yield put(
      stopSubmit(FORM_NAMES.changePassword, {
        currentPassword: i18n.t('common:patient.incorrectPassword'),
      }),
    );
  }
}

export function* handleSubmitNotificationsForm() {
  yield put(startSubmit(FORM_NAMES.notificationsSetting));
  const resource = yield select(getEditedPatientsNotificationsData);

  yield put(requestPutPatientById(resource));
  const { success } = yield race(createSagaRequestRace(requestPutPatientById));
  if (success) {
    yield put(userSetResource({ resource }));
  }

  yield put(stopSubmit(FORM_NAMES.notificationsSetting));
}

export function* handleSendFeedbackForm() {
  yield put(startSubmit(FORM_NAMES.feedback));
  const { category, message } = yield select(getFormValues(FORM_NAMES.feedback));

  yield put(requestSendFeedback({ category, message }));
  const { success } = yield race(createSagaRequestRace(requestSendFeedback));
  if (success) {
    yield put(reset(FORM_NAMES.feedback));
    yield put(
      notificationsAdd({
        id: getUniqId(),
        variant: 'success',
        title: i18n.t('common:feedback.requestTitle'),
        description: i18n.t('common:feedback.requestMsg'),
      }),
    );
  }

  yield put(stopSubmit(FORM_NAMES.feedback));
}

export function* handleAcceptAgreement() {
  const userAcceptedUserAgreement = yield select(getPatientAcceptAgreementData);
  const userId = yield select(getUserId);
  const userResourceType = yield select(getUserResourceType);
  yield put(
    requestPutUserData({ type: userResourceType, id: userId, resource: userAcceptedUserAgreement }),
  );

  const { success } = yield race(createSagaRequestRace(requestPutUserData));
  if (success) {
    yield put(hideModal(ACCEPT_USER_AGREEMENT));
    yield put(userSetResource({ resource: userAcceptedUserAgreement }));
  }
}

export default function*() {
  yield takeEvery(userSubmitChangePasswordForm, handleSubmitChangeForm);
  yield takeEvery(userSubmitFeedbackForm, handleSendFeedbackForm);
  yield takeEvery(userSubmitNotificationsForm, handleSubmitNotificationsForm);
  yield takeEvery(userAcceptAgreement, handleAcceptAgreement);
}
